
import { useMemo, useRef } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { Box, CircularProgress, Grid, Typography, useTheme } from '@mui/material';
import { ITaskContext, TaskProvider, useTaskOverview, useTaskTemplate } from 'hooks/tasks';
import TaskHeader from './TaskHeader';
import TaskTabs from './TaskTabs';
import TaskRequirementsBox from './TaskRequirementsBox';
import { AddRepeatTasksDrawer } from './repeat-tasks';
import SuccessorTaskMissingResourceBox from './SuccessorTaskMissingResourceBox';
import { Ico_Export } from '@sagecentral/core-ui';
import useTaskMetadataExport from 'hooks/tasks/useTaskMetadataExport';
import { CustomCircularProgress } from 'components/shared/layout';
import { useRefresh } from 'hooks/shared';

const TaskNotFound = () => (
    <Typography variant='subtitle1' color='error'>Task not found.</Typography>
);

const TaskLayout = () => {
    const theme = useTheme();
    const [ refreshTemplateIndex, refreshTemplate ] = useRefresh();
    const { taskId } = useParams();
    const location = useLocation();
    const [task, loading, updateTaskOverview, error] = useTaskOverview(taskId ? parseInt(taskId) : null);
    const [template, loadingTemplate, templateError] = useTaskTemplate({
        taskId: taskId ? parseInt(taskId) : null,
        refreshIndex: refreshTemplateIndex
    });
    const [handleExport, working] = useTaskMetadataExport(task?.id, task?.workItem.id, template?.exportSuffix ?? '');

    const btnContainer = useRef<HTMLElement>(null);

    const value = useMemo<ITaskContext>(() => ({
        task,
        template,
        btnContainer: () => btnContainer.current,
        updateTaskOverview,
        refreshTemplate
    }), [task, template, updateTaskOverview]);



    return (
        <Box>
            {/* Skip link for keyboard users - hidden visually but appears on focus */}
            <a 
                href="#main-content"
                style={{
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    padding: 0,
                    margin: '-1px',
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    borderWidth: 0,
                    zIndex: 9999
                }}
                onFocus={(e) => {
                    e.currentTarget.style.position = 'fixed';
                    e.currentTarget.style.width = 'auto';
                    e.currentTarget.style.height = 'auto';
                    e.currentTarget.style.clip = 'auto';
                    e.currentTarget.style.padding = '10px';
                    e.currentTarget.style.margin = '10px';
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.border = '2px solid #333';
                }}
                onBlur={(e) => {
                    e.currentTarget.style.position = 'absolute';
                    e.currentTarget.style.width = '1px';
                    e.currentTarget.style.height = '1px';
                    e.currentTarget.style.clip = 'rect(0, 0, 0, 0)';
                    e.currentTarget.style.padding = '0';
                    e.currentTarget.style.margin = '-1px';
                    e.currentTarget.style.border = 'none';
                }}
            >
                Skip to main content
            </a>
            {loading ? <CustomCircularProgress /> :
                task ?
                    <TaskProvider value={value}>
                        <TaskHeader />
                        <TaskTabs btnContainer={btnContainer} />
                        <AddRepeatTasksDrawer />
                        <Box sx={{
                            display: 'flex'
                        }}>
                            <Grid container sx={{ minWidth: "100%" }}>
                                <Grid item xs={8} >
                                    {/* Add id for skiplink target and ensure proper focus management */}
                                    <div id="main-content" tabIndex={-1} style={{ outline: 'none' }}>
                                        <Outlet />
                                    </div>
                                </Grid>
                                <Grid item xs={4} >
                                    {location.pathname.includes('metadata') && template?.isMetadataExportVisible ?
                                        (working ? <CircularProgress sx={{ top: '0%' }} /> :
                                            <Box sx={{ paddingRight: '32px', display: 'flex', justifyContent: 'flex-end' }}>
                                                <Typography sx={{ display: 'flex', gap: '8px', color: theme.palette.common.black, cursor: 'pointer' }} onClick={handleExport} rel="noopener noreferrer">
                                                    <svg fill="none" height={24} width={24}>{Ico_Export}</svg> Metadata Export
                                                </Typography>
                                            </Box>
                                        )
                                        : null}
                                    <Box sx={{ flexGrow: 1, flexShrink: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', flexBasis: '30%', padding: '32px', paddingTop: '60px', }}>
                                        <TaskRequirementsBox />
                                        <SuccessorTaskMissingResourceBox />
                                    </Box>

                                </Grid>
                            </Grid>

                        </Box>
                    </TaskProvider>
                    : <TaskNotFound />
            }
        </Box>
    );
};

export default TaskLayout;